import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Copy, Check, UserPlus, ChevronRight } from 'lucide-react';
import type { SelfServiceSalesperson, SelfServiceSubmissionRow } from '../../types/selfService';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import { publisherStatusBadge } from '../../components/self-service/campaignSharedHelpers';
import { apiClient } from '../../lib/apiClient';

type SubmissionRow = Pick<
  SelfServiceSubmissionRow,
  'id' | 'business_info' | 'contact_email' | 'campaign' | 'amount_cents'
  | 'service_fee_cents' | 'status' | 'created_at' | 'referral_code'
>;

const PublisherReferralDetailPage: React.FC = () => {
  const { slug } = useSingleStationContext();
  const { salespersonId } = useParams<{ salespersonId: string }>();
  if (!salespersonId) return <Navigate to={`/admin/${slug}/referrals`} replace />;
  return <Inner slug={slug} salespersonId={salespersonId} />;
};

const Inner: React.FC<{ slug: string; salespersonId: string }> = ({ slug, salespersonId }) => {
  const [salesperson, setSalesperson] = React.useState<SelfServiceSalesperson | null>(null);
  const [submissions, setSubmissions] = React.useState<SubmissionRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Two list calls in parallel; client-side filter to scope to this rep.
        const [spData, subData] = await Promise.all([
          apiClient.post<{ rows: SelfServiceSalesperson[] }>('/salespeople/list', { slug }),
          apiClient.post<{ rows: SubmissionRow[] }>('/submissions/list-by-publisher', { slug }),
        ]);
        if (cancelled) return;
        const sp = (spData.rows ?? []).find((r) => r.id === salespersonId) ?? null;
        setSalesperson(sp);
        if (sp) {
          const all = subData.rows ?? [];
          setSubmissions(all.filter((s) => (s.referral_code ?? null) === sp.code));
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, salespersonId]);

  const shareUrl = salesperson
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/${slug}?ref=${salesperson.code}`
    : '';

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  // Roll-up stats. "Approved revenue" only counts captured charges since
  // authorized holds may still get rejected and never become real $.
  // (Bonus plays the rep "gave away" used to be back-calculated as a $
  // discount; under the new model, the discount is delivered as extra ad
  // plays at full price, so dollar discount given is always 0. We could
  // eventually surface "bonus plays delivered" once we track per-submission
  // CPM — TODO.)
  const stats = React.useMemo(() => {
    // Status enum migrated: legacy 'captured' → 'approved', 'authorized' → 'payment_authorized'.
    const captured = submissions.filter((s) => s.status === 'approved');
    const capturedRevenue = captured.reduce((acc, s) => acc + s.amount_cents, 0);
    const pendingCount = submissions.filter((s) => s.status === 'payment_authorized').length;
    return {
      total: submissions.length,
      capturedCount: captured.length,
      pendingCount,
      capturedRevenueDollars: capturedRevenue / 100,
    };
  }, [submissions]);

  return (
    <div className="max-w-5xl">
      <Link to={`/admin/${slug}/referrals`} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to referrals
      </Link>

        {loading ? (
          <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>
        ) : !salesperson ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <UserPlus className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-700 font-medium">Salesperson not found</p>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Salesperson</div>
                <h1 className="text-2xl font-semibold text-slate-900">{salesperson.name}</h1>
                <div className="mt-2 flex items-center gap-3 text-sm flex-wrap">
                  <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 font-mono text-[12px]">{salesperson.code}</code>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded ${salesperson.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                    {salesperson.active ? 'Active' : 'Disabled'}
                  </span>
                  {salesperson.default_discount_percent != null && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200">
                      Offers +{salesperson.default_discount_percent}% bonus plays
                    </span>
                  )}
                  {salesperson.email && <span className="text-slate-600">{salesperson.email}</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-md whitespace-nowrap"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy share link'}
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Stat label="Total submissions" value={stats.total.toString()} />
              <Stat label="Approved" value={stats.capturedCount.toString()} />
              <Stat label="Pending review" value={stats.pendingCount.toString()} />
              <Stat
                label="Approved revenue"
                value={`$${stats.capturedRevenueDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              />
            </div>

            <div className="mb-3">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Attributed campaigns</h2>
            </div>

            {submissions.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
                <p className="text-slate-700 font-medium">No campaigns attributed yet</p>
                <p className="text-sm text-slate-500 mt-1">When an advertiser uses this code, their campaign will show up here.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {submissions.map((s) => {
                  const total = (s.amount_cents / 100).toFixed(2);
                  return (
                    <li key={s.id}>
                      <Link
                        to={`/admin/${slug}/submissions/${s.id}`}
                        className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-400 hover:shadow-sm transition-all"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-base font-semibold text-slate-900 truncate">
                              {s.business_info?.businessName || 'Advertiser'}
                            </span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${publisherStatusBadge(s.status).className}`}>
                              {publisherStatusBadge(s.status).label}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500">
                            ${total} · {new Date(s.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
        </>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5">
    <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</div>
    <div className="text-2xl font-bold text-slate-900">{value}</div>
  </div>
);

export default PublisherReferralDetailPage;
